import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Flashcard from './Flashcard';
import { FiChevronLeft, FiChevronRight, FiShuffle, FiRotateCcw, FiArrowRight, FiTarget, FiTrendingUp, FiAward } from 'react-icons/fi';
import { subjects } from '../data/flashcards';
import './FlashcardDeck.css';

const FlashcardDeck = ({ 
  flashcards, 
  currentCardIndex, 
  setCurrentCardIndex, 
  onCardAnswer,
  stats,
  selectedSubject,
  onBackToSubjects,
  onNextQuiz
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [shuffledCards, setShuffledCards] = useState(flashcards);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    setShuffledCards(flashcards);
  }, [flashcards]);

  const nextCard = () => {
    if (currentCardIndex < shuffledCards.length - 1) {
      setDirection(1);
      setCurrentCardIndex(currentCardIndex + 1);
      setIsFlipped(false);
    }
  };

  const prevCard = () => {
    if (currentCardIndex > 0) {
      setDirection(-1);
      setCurrentCardIndex(currentCardIndex - 1);
      setIsFlipped(false);
    }
  };

  const shuffleCards = () => {
    const shuffled = [...flashcards].sort(() => Math.random() - 0.5);
    setShuffledCards(shuffled);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setDirection(0);
  };

  const resetDeck = () => {
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setDirection(0);
  };

  const handleAnswer = (isCorrect) => {
    onCardAnswer(isCorrect);
    setTimeout(() => {
      if (currentCardIndex >= shuffledCards.length - 1) {
        // If this was the last card, move beyond the array to trigger completion
        setCurrentCardIndex(shuffledCards.length);
      } else {
        nextCard();
      }
    }, 800);
  };

  const getNextSubject = () => {
    if (selectedSubject === 'all') return null;
    
    const currentIndex = subjects.findIndex(s => s.key === selectedSubject);
    if (currentIndex === -1 || currentIndex === subjects.length - 1) return null;
    
    return subjects[currentIndex + 1];
  };

  const nextSubject = getNextSubject();
  const accuracy = stats.cardsStudied > 0 ? Math.round((stats.correctAnswers / stats.cardsStudied) * 100) : 0;
  const completedThisSession = shuffledCards.length;

  const currentCard = shuffledCards[currentCardIndex];
  const progress = ((currentCardIndex + 1) / shuffledCards.length) * 100;

  const slideVariants = {
    hiddenRight: {
      x: "100%",
      opacity: 0,
    },
    hiddenLeft: {
      x: "-100%",
      opacity: 0,
    },
    visible: {
      x: "0",
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.15,
      }
    }
  };

  if (!currentCard) {
    return (
      <div className="deck-container">
        <div className="completion-screen">
          <motion.div 
            className="completion-content"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <div className="completion-header">
              <div className="completion-icon">🎉</div>
              <h2>Quiz Complete!</h2>
              <p>Great job! You've finished studying all {completedThisSession} cards</p>
            </div>

            <div className="detailed-stats">
              <div className="stat-card">
                <div className="stat-icon">
                  <FiTarget />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stats.correctAnswers}</span>
                  <span className="stat-label">Correct Answers</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">
                  <FiTrendingUp />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{accuracy}%</span>
                  <span className="stat-label">Accuracy</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">
                  <FiAward />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stats.streak}</span>
                  <span className="stat-label">Current Streak</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🔥</div>
                <div className="stat-info">
                  <span className="stat-value">{stats.bestStreak}</span>
                  <span className="stat-label">Best Streak</span>
                </div>
              </div>
            </div>

            <div className="completion-actions">
              <div className="primary-actions">
                {nextSubject && onNextQuiz && (
                  <motion.button 
                    className="btn btn-primary next-quiz-btn"
                    onClick={() => onNextQuiz(nextSubject.key)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="next-subject-info">
                      <span className="next-subject-icon">{nextSubject.icon}</span>
                      <span>Next: {nextSubject.name}</span>
                    </span>
                    <FiArrowRight />
                  </motion.button>
                )}
                
                <motion.button 
                  className="btn btn-secondary"
                  onClick={resetDeck}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FiRotateCcw />
                  Study Again
                </motion.button>
              </div>
              
              <div className="secondary-actions">
                <button className="btn btn-outline" onClick={shuffleCards}>
                  <FiShuffle />
                  Shuffle & Restart
                </button>
                
                <button className="btn btn-outline" onClick={onBackToSubjects}>
                  All Subjects
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="deck-container">
      <div className="deck-header">
        <div className="progress-section">
          <div className="progress-info">
            <span className="progress-text">
              {currentCardIndex + 1} of {shuffledCards.length}
            </span>
          </div>
          <div className="progress-bar">
            <motion.div 
              className="progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
        
        <div className="deck-actions">
          <motion.button 
            className="action-btn"
            onClick={shuffleCards}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Shuffle Cards"
          >
            <FiShuffle />
          </motion.button>
          <motion.button 
            className="action-btn"
            onClick={resetDeck}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Reset Deck"
          >
            <FiRotateCcw />
          </motion.button>
        </div>
      </div>

      <div className="flashcard-container">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentCardIndex}
            custom={direction}
            variants={slideVariants}
            initial={direction > 0 ? "hiddenRight" : direction < 0 ? "hiddenLeft" : "visible"}
            animate="visible"
            exit="exit"
            className="flashcard-wrapper"
          >
            <Flashcard
              card={currentCard}
              isFlipped={isFlipped}
              setIsFlipped={setIsFlipped}
              onAnswer={handleAnswer}
              flashcards={shuffledCards}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="navigation-controls">
        <motion.button
          className={`nav-btn ${currentCardIndex === 0 ? 'disabled' : ''}`}
          onClick={prevCard}
          disabled={currentCardIndex === 0}
          whileHover={currentCardIndex > 0 ? { scale: 1.05 } : {}}
          whileTap={currentCardIndex > 0 ? { scale: 0.95 } : {}}
        >
          <FiChevronLeft />
          <span>Previous</span>
        </motion.button>

        <div className="card-indicators">
          {shuffledCards.slice(Math.max(0, currentCardIndex - 2), currentCardIndex + 3).map((_, index) => {
            const actualIndex = Math.max(0, currentCardIndex - 2) + index;
            return (
              <motion.div
                key={actualIndex}
                className={`indicator ${actualIndex === currentCardIndex ? 'active' : ''}`}
                whileHover={{ scale: 1.2 }}
                onClick={() => {
                  setDirection(actualIndex > currentCardIndex ? 1 : -1);
                  setCurrentCardIndex(actualIndex);
                  setIsFlipped(false);
                }}
                layoutId={`indicator-${actualIndex}`}
              />
            );
          })}
        </div>

        <motion.button
          className={`nav-btn ${currentCardIndex === shuffledCards.length - 1 ? 'disabled' : ''}`}
          onClick={nextCard}
          disabled={currentCardIndex === shuffledCards.length - 1}
          whileHover={currentCardIndex < shuffledCards.length - 1 ? { scale: 1.05 } : {}}
          whileTap={currentCardIndex < shuffledCards.length - 1 ? { scale: 0.95 } : {}}
        >
          <span>Next</span>
          <FiChevronRight />
        </motion.button>
      </div>
    </div>
  );
};

export default FlashcardDeck; 